import { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Mail, Save } from "lucide-react";

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);

  const load = async () => {
    const [t, l] = await Promise.all([api.get("/admin/email-templates"), api.get("/admin/email-logs")]);
    setTemplates(t.data); setLogs(l.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (tpl) => {
    await api.put(`/admin/email-templates/${tpl.id}`, { subject: tpl.subject, body: tpl.body });
    toast.success(`"${tpl.name}" saved`);
  };
  const update = (id, field, value) => setTemplates((ts) => ts.map((t) => t.id === id ? { ...t, [field]: value } : t));

  return (
    <AdminLayout title="Email Templates">
      <div className="bg-[#C79A3B]/10 border border-[#C79A3B]/30 rounded-lg p-3 text-xs text-[#101820]/80">
        Templates are editable and support placeholders: <code className="font-mono-data">{"{first_name}"}</code>, <code className="font-mono-data">{"{job_title}"}</code>, <code className="font-mono-data">{"{reference}"}</code>, <code className="font-mono-data">{"{interview_date}"}</code>. Emails are <b>sent live</b> to real candidate addresses via Resend and logged below. Demo candidates (example-demo.ie) are logged only, not sent.
      </div>
      <Tabs defaultValue="templates">
        <TabsList className="bg-[#101820]/5"><TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger><TabsTrigger value="logs" data-testid="tab-logs">Sent Log ({logs.length})</TabsTrigger></TabsList>
        <TabsContent value="templates" className="mt-4 grid lg:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div key={t.id} data-testid={`template-${t.key}`} className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3"><Mail className="h-4 w-4 text-[#C79A3B]" /><h3 className="font-heading font-semibold text-[#101820]">{t.name}</h3></div>
              <label className="text-xs text-muted-foreground">Subject</label>
              <Input data-testid={`subject-${t.key}`} value={t.subject} onChange={(e) => update(t.id, "subject", e.target.value)} className="mt-1 mb-3 bg-[#FAF6EE]" />
              <label className="text-xs text-muted-foreground">Body</label>
              <Textarea data-testid={`body-${t.key}`} value={t.body} onChange={(e) => update(t.id, "body", e.target.value)} rows={7} className="mt-1 bg-[#FAF6EE] font-mono-data text-xs" />
              <Button data-testid={`save-${t.key}`} onClick={() => save(t)} className="mt-3 bg-[#101820] text-[#FAF6EE]"><Save className="h-4 w-4 mr-1" /> Save</Button>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="logs" className="mt-4">
          <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm divide-y divide-[#C79A3B]/10">
            {logs.length === 0 && <p className="p-6 text-muted-foreground text-sm">No emails sent yet. Change a candidate's status to trigger a (simulated) email.</p>}
            {logs.map((l) => (
              <div key={l.id} className="p-4">
                <div className="flex items-center justify-between"><span className="font-medium text-[#101820] text-sm">{l.subject}</span><span className="text-[10px] bg-[#C79A3B]/15 text-[#8a6a1e] px-2 py-0.5 rounded-full">{l.status}</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">To: {l.to} · {new Date(l.at).toLocaleString()}</div>
                <p className="text-xs text-[#101820]/70 mt-2 whitespace-pre-wrap line-clamp-3">{l.body}</p>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
